import React, { useEffect, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useListTopicsQuery, useListLearningPathwaysQuery } from 'src/redux/slices/api/su';
import { useClientId, useSelLocale } from 'src/contexts/ClientContext';
import { useAppSelector } from 'src/redux/hooks';
import LoadingView from 'src/components/LoadingView';
import ErrorView from 'src/components/ErrorView';
import { getStatusCodeFromError } from 'src/utils/error';
import { ROUTES, ROUTE_SEARCH_PARAM } from 'src/constants/route';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import useMetrics from 'src/metrics/useUserMetrics';
import { FeatureName } from 'src/metrics/metricsFields';
import LearningPathwaysCarousel from 'src/components/carousel/LearningPathwaysCarousel';
import TopicsCarousel from 'src/components/carousel/TopicsCarousel';
import { useBundle } from '@amzn/react-arb-tools';
import { t } from 'src/utils/translation';
import { BUNDLE_NAME, HOMEPAGE_STRING_ID } from 'src/constants/localization';
import { TopicState } from 'src/types/state/topic';
import { LearningPathwayState } from 'src/types/state/learningPathway';
import { getAppRoute } from 'src/utils/routing';
import { LearningModuleModal } from 'src/components/learning-module';
import { EntityType } from 'src/constants/entity';
import { removeSearchParam } from 'src/utils/searchParams';
import HomepageCarouselsSection from './HomepageCarouselsSection';

/** HomePage Component */
const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { viewMetric } = useMetrics();
  const [homepageBundle] = useBundle(BUNDLE_NAME.HOMEPAGE);
  const topicsById = useAppSelector((rootState) => rootState.topics.byId);
  const learningPathwaysById = useAppSelector((rootState) => rootState.learningPathways.byId);
  const [searchParams] = useSearchParams();
  const learningModuleId = searchParams.get(ROUTE_SEARCH_PARAM.LEARNING_MODULE_ID);
  const clientId = useClientId();
  const selLocale = useSelLocale();

  // API queries
  const {
    data: topicsData,
    isLoading: isTopicsLoading,
    isError: isTopicsError,
    error: topicsErrorObj,
  } = useListTopicsQuery({ clientId, selLocale });

  const {
    data: learningPathwaysData,
    isLoading: isLearningPathwaysLoading,
    isError: isLearningPathwaysError,
    error: learningPathwaysErrorObj,
  } = useListLearningPathwaysQuery({ clientId, selLocale });

  // Track page view on mount
  useEffect(() => {
    viewMetric({ feature: FeatureName.HomePage, metadata: { path: location.pathname } });
  }, []);

  // Process topics data
  const topicCards = useMemo(() => {
    const topicIds = topicsData?.topics?.map((topic) => topic.topicId) || [];
    return topicIds
      .map((id) => topicsById[id])
      .filter(Boolean)
      .map((topic: TopicState) => ({
        id: topic.topicId,
        title: topic.title,
        description: topic.preview?.previewDescription || '',
        thumbnailLink: topic.preview?.imgSrc || '',
        buttonProps: {
          children: t(homepageBundle, HOMEPAGE_STRING_ID.LEARN_MORE_BUTTON),
          label: t(homepageBundle, HOMEPAGE_STRING_ID.LEARN_MORE_BUTTON),
          onClick: () => navigate(getAppRoute(`${ROUTES.LEARN.TOPIC}/${topic.topicId}`)),
        },
      }));
  }, [topicsData, topicsById, homepageBundle]);

  // Process learning pathways data
  const learningPathwayCards = useMemo(() => {
    const pathwayIds = learningPathwaysData?.learningPathways?.map((pathway) => pathway.learningPathwayId) || [];
    return pathwayIds
      .map((id) => learningPathwaysById[id])
      .filter(Boolean)
      .map((pathway: LearningPathwayState) => {
        const { data } = pathway;

        return {
          id: data.learningPathwayId,
          title: data.title,
          previewDescription: data.description,
          imageSrc: data.thumbnail || data.imageSrc || '',
          contentVariant: 'Video' as const,
          stats: {
            totalSections: data.learningPathwayStats?.totalSections || 0,
            totalModules: (data.learningPathwayStats?.totalDocuments || 0)
              + (data.learningPathwayStats?.totalVideos || 0),
            totalDuration: data.learningPathwayStats?.totalDuration || 0,
            totalDocuments: data.learningPathwayStats?.totalDocuments || 0,
            totalVideos: data.learningPathwayStats?.totalVideos || 0,
          },
          onClick: () => navigate(getAppRoute(`${ROUTES.LEARN.LEARNING_PATHWAY}/${data.learningPathwayId}`)),
        };
      });
  }, [learningPathwaysData, learningPathwaysById, navigate]);

  // Render section helper
  const renderSection = (isLoading: boolean, isError: boolean, errorObj: unknown, component: React.ReactNode) => {
    if (isLoading) return <LoadingView />;
    if (isError) return <ErrorView statusCode={getStatusCodeFromError(errorObj)} />;
    return component;
  };

  const homepageCarouselsComponent = useMemo(
    () => (
      <HomepageCarouselsSection
        bundleName={BUNDLE_NAME.HOMEPAGE}
        className="mb-[36px]"
      />
    ),
    [],
  );

  const removeModuleSearchParam = useCallback(() => {
    navigate({ search: removeSearchParam(searchParams, ROUTE_SEARCH_PARAM.LEARNING_MODULE_ID) }, { replace: true });
  }, [navigate, searchParams]);

  const learningModuleModalComponent = useMemo(() => {
    if (!learningModuleId) return null;
    return (
      <LearningModuleModal
        learningModuleId={learningModuleId}
        onModalClose={removeModuleSearchParam}
        parentId="homepage-modules-carousel"
        parentType={EntityType.HomePage}
      />
    );
  }, [learningModuleId, removeModuleSearchParam]);

  return (
    <div className="px-su-page-padding" data-testid="homepage">
      {homepageBundle && (
        <Helmet>
          <title>{t(homepageBundle, HOMEPAGE_STRING_ID.SEO_TITLE)}</title>
          <meta name="description" content={t(homepageBundle, HOMEPAGE_STRING_ID.SEO_DESCRIPTION)} />
          <link rel="canonical" href={`${window.location.origin}${ROUTES.LEARN.HOME}`} />
        </Helmet>
      )}
      <section className="mb-[36px]" data-testid="topics-section">
        <h2 className="mx-[28px] !mb-su-space-05 !pt-[36px] !text-su-heading-400 !font-bold">
          {t(homepageBundle, HOMEPAGE_STRING_ID.TOPICS_CAROUSEL_TITLE)}
        </h2>
        {renderSection(isTopicsLoading, isTopicsError, topicsErrorObj, <TopicsCarousel topics={topicCards} />)}
      </section>

      <section className="mb-[36px]" data-testid="pathways-section">
        <h2 className="mx-[28px] !mb-su-space-05 !pt-[36px] !text-su-heading-400 !font-bold">
          {t(homepageBundle, HOMEPAGE_STRING_ID.LEARNING_PATHWAY_CAROUSEL_TITLE)}
        </h2>
        {renderSection(
          isLearningPathwaysLoading,
          isLearningPathwaysError,
          learningPathwaysErrorObj,
          <LearningPathwaysCarousel pathways={learningPathwayCards} />,
        )}
      </section>

      {homepageCarouselsComponent}

      {learningModuleId && learningModuleModalComponent}
    </div>
  );
};

export default HomePage;
